/**
 * COUNTRY SELECT — Autocomplete su tutti i Country del backend, lazy load
 * della lista al primo mount. Riusabile in form e in detail inline.
 *
 * Pattern store-driven (no useAsyncAutocomplete): i countries sono pochi
 * (decine) e cached "load-once" nello slice `geography`, quindi una
 * normale `<Autocomplete>` MUI con `options` letta da store basta.
 *
 * Reset region on country change: gestito dal **parent** che riceve il
 * nuovo countryId via `onChange` e setta sia countryId che regionId del
 * suo state. Non incapsulato qui per non vincolare l'API.
 *
 * Modalità `inlineEditable`: se true, in view mode mostra solo la label
 * del country (flat, no border) e apre l'Autocomplete vero su doppio
 * click — coerente con `EditableField`. Default false (= dropdown
 * sempre visibile, da usare nei form drawer dove l'edit mode è implicito).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCountries } from '../entities/geography/geographyThunks';
import type { Country } from '../entities/geography/geographyTypes';

export interface CountrySelectProps {
  value: number | null | undefined;
  onChange: (countryId: number | null) => void;
  /** Default: t('address.country') */
  label?: string;
  required?: boolean;
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  /**
   * Se true, view-mode flat (label) + edit on double-click. Coerente con
   * EditableField. Per l'uso inline in detail page (es. AddressListEditor).
   */
  inlineEditable?: boolean;
}

export function CountrySelect({
  value,
  onChange,
  label,
  required,
  fullWidth,
  className,
  disabled,
  inlineEditable,
}: CountrySelectProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const countries = useAppSelector((s) => s.geography.countries);
  const status = useAppSelector((s) => s.geography.countriesStatus);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    // Lazy load: il thunk ha `condition` che salta se già loading/succeeded,
    // quindi il dispatch è no-op cross-mount.
    dispatch(fetchCountries());
  }, [dispatch]);

  const selected: Country | null = useMemo(() => {
    if (value == null) return null;
    return countries.find((c) => c.countryId === value) ?? null;
  }, [countries, value]);

  // Inline editable: in view mode mostra solo la label (flat) + double click
  // per entrare in edit. In edit mode renderizza l'Autocomplete come default.
  if (inlineEditable && !editing) {
    return (
      <div
        className={`editable-field editable-field--view editable-field--flat ${className ?? ''}`}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? -1 : 0}
        onDoubleClick={() => { if (!disabled) setEditing(true); }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); }
        }}
        title={disabled ? undefined : t('editableField.doubleClickToEdit')}
      >
        <span className="editable-field__value">{selected?.title ?? '—'}</span>
      </div>
    );
  }

  return (
    <Autocomplete
      options={countries}
      value={selected}
      onChange={(_, next) => {
        onChange(next?.countryId ?? null);
        if (inlineEditable) setEditing(false);
      }}
      onBlur={() => { if (inlineEditable) setEditing(false); }}
      getOptionLabel={(opt) => opt.title}
      isOptionEqualToValue={(a, b) => a.countryId === b.countryId}
      loading={status === 'loading'}
      disabled={disabled}
      fullWidth={fullWidth}
      openOnFocus={inlineEditable}
      className={className}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={inlineEditable}
          label={label ?? t('address.country')}
          required={required}
        />
      )}
    />
  );
}
